import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Upload, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import api, { getFileUrl } from '../../lib/api';

export default function ServiceManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title:'',slug:'',description:'',content:'',icon:'',position:0,is_active:true });
  const [coverFile, setCoverFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetch = useCallback(() => { api.get('/services').then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const resetForm = () => { setForm({ title:'',slug:'',description:'',content:'',icon:'',position:0,is_active:true }); setCoverFile(null); setGalleryFiles([]); setEditing(null); setShowForm(false); };

  const handleEdit = (s) => {
    setEditing(s);
    setForm({
      title: s.title||'', slug: s.slug||'', description: s.description||'',
      content: s.content||'', icon: s.icon||'',
      position: parseInt(s.position)||0, is_active: s.is_active===true||s.is_active==='true'||s.is_active==='True'
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Başlık gerekli'); return; }
    setIsSubmitting(true);
    try {
      let cover = editing?.cover_image || '';
      if (coverFile) { const fd = new FormData(); fd.append('file', coverFile); const r = await api.post('/uploads', fd); cover = r.data.path; }
      let existingImages = editing?.images || [];
      if (typeof existingImages === 'string') existingImages = JSON.parse(existingImages || '[]');
      let newImages = [];
      for (const file of galleryFiles) { const fd = new FormData(); fd.append('file', file); const r = await api.post('/uploads', fd); newImages.push(r.data.path); }
      const payload = { ...form, cover_image: cover, images: [...existingImages, ...newImages] };
      if (editing) { await api.put(`/services/${editing.id}`, payload); toast.success('Güncellendi'); }
      else { await api.post('/services', payload); toast.success('Eklendi'); }
      resetForm(); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (id) => { if (!window.confirm('Silmek istediğinize emin misiniz?')) return; try { await api.delete(`/services/${id}`); toast.success('Silindi'); fetch(); } catch { toast.error('Hata'); } };

  return (
    <div data-testid="admin-service-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Hizmet Yönetimi</h1>
          <p className="text-sm text-muted-foreground mt-1">Hizmetlerimiz menüsü altındaki sayfaları yönetin</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium"><Plus className="w-4 h-4" /> Yeni Hizmet</button>
      </div>

      {showForm && (
        <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">{editing ? 'Hizmet Düzenle' : 'Yeni Hizmet Sayfası'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Inp label="Hizmet Adı *" value={form.title} onChange={v => setForm(f=>({...f,title:v}))} placeholder="Örn: Tadilat Hizmetleri" />
              <Inp label="Slug" value={form.slug} onChange={v => setForm(f=>({...f,slug:v}))} placeholder="otomatik oluşturulur" />
              <Inp label="İkon" value={form.icon} onChange={v => setForm(f=>({...f,icon:v}))} placeholder="Örn: home, building, wrench" />
              <Inp label="Sıra" type="number" value={form.position} onChange={v => setForm(f=>({...f,position:parseInt(v)||0}))} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Kısa Açıklama</label>
              <textarea value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} rows={2} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Detaylı İçerik</label>
              <textarea value={form.content} onChange={e => setForm(f=>({...f,content:e.target.value}))} rows={8} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm resize-none" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Kapak Görseli</label>
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary"><Upload className="w-4 h-4" /> Görsel Seç<input type="file" accept="image/*" className="hidden" onChange={e => setCoverFile(e.target.files[0])} /></label>
                <span className="text-xs text-muted-foreground ml-2">{coverFile?.name || ''}</span>
                {editing?.cover_image && <img src={getFileUrl(editing.cover_image)} alt="" className="mt-2 h-16 rounded-lg object-cover" />}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Ek Görseller</label>
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary"><Upload className="w-4 h-4" /> Görseller Ekle<input type="file" accept="image/*" multiple className="hidden" onChange={e => setGalleryFiles(Array.from(e.target.files))} /></label>
                {galleryFiles.length > 0 && <p className="text-xs text-muted-foreground mt-1">{galleryFiles.length} dosya seçildi</p>}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f=>({...f,is_active:e.target.checked}))} className="rounded" /> Aktif</label>
            <div className="flex gap-3">
              <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50" data-testid="admin-save-button">{isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}</button>
              <button type="button" onClick={resetForm} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      : items.length === 0 ? <div className="text-center py-10 text-muted-foreground">Henüz hizmet eklenmemiş</div>
      : <div className="space-y-3">{items.map((s, i) => (
        <div key={s.id} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border">
          <span className="text-lg font-heading font-bold text-[hsl(var(--gold))] tabular-nums w-8">{String(i+1).padStart(2,'0')}</span>
          <div className="w-16 h-12 rounded-lg overflow-hidden bg-secondary shrink-0">{s.cover_image && <img src={getFileUrl(s.cover_image)} alt="" className="w-full h-full object-cover" />}</div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">{s.title}</div>
            <div className="text-xs text-muted-foreground truncate">{s.description || 'Açıklama yok'}</div>
          </div>
          <span className={`px-2 py-0.5 text-xs rounded-full ${s.is_active===true||s.is_active==='true'||s.is_active==='True' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
            {s.is_active===true||s.is_active==='true'||s.is_active==='True' ? 'Aktif' : 'Pasif'}
          </span>
          <div className="flex gap-1">
            <button onClick={() => handleEdit(s)} className="p-2 rounded-lg hover:bg-secondary"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => handleDelete(s.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      ))}</div>}
    </div>
  );
}

const Inp = ({ label, value, onChange, type='text', placeholder }) => (<div><label className="block text-sm font-medium mb-1.5">{label}</label><input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" /></div>);
